import { useEffect, useState } from 'react';
import { getLandingConfig, updateLandingConfig, uploadAdminImage } from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';

const DEFAULT_HERO_BANNER = {
  heading: '',
  subheading: '',
  ctaText: '',
  ctaLink: '',
  heroImageDesktop: '',
  heroImageMobile: '',
  showVideo: false,
  videoUrl: '',
};

const DEFAULT_SECTIONS = [
  {
    sectionKey: 'hero',
    enabled: true,
    order: 1,
    content: {
      heading: '',
      subheading: '',
      ctaText: '',
      ctaLink: '',
      heroImageDesktop: '',
      heroImageMobile: '',
      showVideo: false,
      videoUrl: '',
      showSearchBar: true,
      heroBanners: [{ ...DEFAULT_HERO_BANNER }],
    },
  },
  { sectionKey: 'destinations', enabled: true, order: 2, content: { heading: '', subheading: '', maxItems: 8 } },
  { sectionKey: 'trending', enabled: true, order: 3, content: { heading: '', subheading: '', maxItems: 6 } },
  { sectionKey: 'themes', enabled: true, order: 4, content: { heading: '', subheading: '' } },
  { sectionKey: 'listing', enabled: true, order: 5, content: { heading: '', subheading: '', defaultSort: 'popularity', pageSize: 6 } },
  { sectionKey: 'offers', enabled: true, order: 6, content: { heading: '', subheading: '', maxItems: 3 } },
  { sectionKey: 'reviews', enabled: true, order: 7, content: { heading: '', subheading: '', maxItems: 6 } },
  { sectionKey: 'journal', enabled: true, order: 8, content: { heading: '', subheading: '', maxItems: 4 } },
  { sectionKey: 'cta', enabled: true, order: 9, content: { heading: '', subheading: '', buttonText: '', buttonLink: '' } },
];

const AdminPackageLanding = () => {
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await getLandingConfig();
      const data = res?.data || res || {};
      setSeoTitle(data.seo_title || '');
      setSeoDescription(data.seo_description || '');
      let nextSections = [];
      if (Array.isArray(data.sections)) {
        nextSections = data.sections;
      } else if (typeof data.sections === 'string') {
        try {
          const parsed = JSON.parse(data.sections);
          nextSections = Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          nextSections = [];
        }
      }
      const normalized = (nextSections.length ? nextSections : DEFAULT_SECTIONS).map((section) => {
        if (String(section.sectionKey).toLowerCase() !== 'hero') return section;
        const content = section.content || {};
        const banners = Array.isArray(content.heroBanners) ? content.heroBanners : [];
        const fallbackBanner =
          content.heading || content.subheading || content.heroImageDesktop
            ? [
                {
                  heading: content.heading || '',
                  subheading: content.subheading || '',
                  ctaText: content.ctaText || '',
                  ctaLink: content.ctaLink || '',
                  heroImageDesktop: content.heroImageDesktop || '',
                  heroImageMobile: content.heroImageMobile || '',
                  showVideo: content.showVideo || false,
                  videoUrl: content.videoUrl || '',
                },
              ]
            : [];
        return {
          ...section,
          content: {
            ...content,
            heroBanners: banners.length ? banners : fallbackBanner.length ? fallbackBanner : [{ ...DEFAULT_HERO_BANNER }],
          },
        };
      });
      setSections(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load landing config');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateSection = (idx, patch) => {
    setSections((prev) => prev.map((section, index) => (index === idx ? { ...section, ...patch } : section)));
  };

  const updateContent = (idx, patch) => {
    setSections((prev) =>
      prev.map((section, index) =>
        index === idx ? { ...section, content: { ...section.content, ...patch } } : section
      )
    );
  };

  const updateHeroBanner = (sectionIdx, bannerIdx, patch) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIdx) return section;
        const content = section.content || {};
        const banners = Array.isArray(content.heroBanners) ? content.heroBanners : [];
        const nextBanners = banners.map((banner, bIdx) => (bIdx === bannerIdx ? { ...banner, ...patch } : banner));
        return { ...section, content: { ...content, heroBanners: nextBanners } };
      })
    );
  };

  const addHeroBanner = (sectionIdx) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIdx) return section;
        const content = section.content || {};
        const banners = Array.isArray(content.heroBanners) ? content.heroBanners : [];
        return { ...section, content: { ...content, heroBanners: [...banners, { ...DEFAULT_HERO_BANNER }] } };
      })
    );
  };

  const removeHeroBanner = (sectionIdx, bannerIdx) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIdx) return section;
        const content = section.content || {};
        const banners = Array.isArray(content.heroBanners) ? content.heroBanners : [];
        const next = banners.filter((_, idx) => idx !== bannerIdx);
        return { ...section, content: { ...content, heroBanners: next.length ? next : [{ ...DEFAULT_HERO_BANNER }] } };
      })
    );
  };

  const uploadImage = async (idx, key, file) => {
    if (!file) return;
    try {
      const res = await uploadAdminImage(file);
      updateContent(idx, { [key]: res.path });
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  const uploadHeroBannerImage = async (sectionIdx, bannerIdx, key, file) => {
    if (!file) return;
    try {
      const res = await uploadAdminImage(file);
      updateHeroBanner(sectionIdx, bannerIdx, { [key]: res.path });
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await updateLandingConfig({
        seo_title: seoTitle,
        seo_description: seoDescription,
        sections,
      });
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Landing Page Builder</h1>
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save config'}
        </Button>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">SEO</h2>
        <input
          className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="SEO title"
          value={seoTitle}
          onChange={(e) => setSeoTitle(e.target.value)}
        />
        <textarea
          className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          rows={3}
          placeholder="SEO description"
          value={seoDescription}
          onChange={(e) => setSeoDescription(e.target.value)}
        />
      </div>
      <div className="space-y-4">
        {sections.map((section, idx) => {
          const key = String(section.sectionKey || '').toLowerCase();
          return (
          <div key={section.sectionKey} className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-900">{section.sectionKey}</h3>
              <div className="flex items-center gap-3 text-xs">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={section.enabled !== false}
                    onChange={(e) => updateSection(idx, { enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <input
                  className="w-20 rounded-xl border border-slate-200 px-2 py-1"
                  placeholder="Order"
                  value={section.order}
                  onChange={(e) => updateSection(idx, { order: e.target.value })}
                />
              </div>
            </div>

            {key === 'hero' && (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <input
                    className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Default heading"
                    value={section.content.heading}
                    onChange={(e) => updateContent(idx, { heading: e.target.value })}
                  />
                  <input
                    className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Default subheading"
                    value={section.content.subheading}
                    onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                  />
                  <input
                    className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Default CTA text"
                    value={section.content.ctaText}
                    onChange={(e) => updateContent(idx, { ctaText: e.target.value })}
                  />
                  <input
                    className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Default CTA link"
                    value={section.content.ctaLink}
                    onChange={(e) => updateContent(idx, { ctaLink: e.target.value })}
                  />
                  <input
                    className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Default video URL"
                    value={section.content.videoUrl}
                    onChange={(e) => updateContent(idx, { videoUrl: e.target.value })}
                  />
                  <div className="flex items-center gap-2 text-xs">
                    <input type="file" onChange={(e) => uploadImage(idx, 'heroImageDesktop', e.target.files?.[0])} />
                    <span>Default desktop image</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <input type="file" onChange={(e) => uploadImage(idx, 'heroImageMobile', e.target.files?.[0])} />
                    <span>Default mobile image</span>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={section.content.showSearchBar !== false}
                      onChange={(e) => updateContent(idx, { showSearchBar: e.target.checked })}
                    />
                    Show search bar
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={section.content.showVideo === true}
                      onChange={(e) => updateContent(idx, { showVideo: e.target.checked })}
                    />
                    Show default video button
                  </label>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Hero banners</p>
                    <Button variant="outline" size="sm" onClick={() => addHeroBanner(idx)}>
                      Add banner
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {(section.content.heroBanners || []).map((banner, bannerIdx) => (
                      <div key={`banner-${bannerIdx}`} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                            Banner {bannerIdx + 1}
                          </p>
                          <button
                            type="button"
                            onClick={() => removeHeroBanner(idx, bannerIdx)}
                            className="text-xs font-semibold text-rose-600"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <input
                            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Heading"
                            value={banner.heading || ''}
                            onChange={(e) => updateHeroBanner(idx, bannerIdx, { heading: e.target.value })}
                          />
                          <input
                            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Subheading"
                            value={banner.subheading || ''}
                            onChange={(e) => updateHeroBanner(idx, bannerIdx, { subheading: e.target.value })}
                          />
                          <input
                            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                            placeholder="CTA text"
                            value={banner.ctaText || ''}
                            onChange={(e) => updateHeroBanner(idx, bannerIdx, { ctaText: e.target.value })}
                          />
                          <input
                            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                            placeholder="CTA link"
                            value={banner.ctaLink || ''}
                            onChange={(e) => updateHeroBanner(idx, bannerIdx, { ctaLink: e.target.value })}
                          />
                          <input
                            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Video URL"
                            value={banner.videoUrl || ''}
                            onChange={(e) => updateHeroBanner(idx, bannerIdx, { videoUrl: e.target.value })}
                          />
                          <div className="flex items-center gap-2 text-xs">
                            <input
                              type="file"
                              onChange={(e) => uploadHeroBannerImage(idx, bannerIdx, 'heroImageDesktop', e.target.files?.[0])}
                            />
                            <span>Desktop image</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <input
                              type="file"
                              onChange={(e) => uploadHeroBannerImage(idx, bannerIdx, 'heroImageMobile', e.target.files?.[0])}
                            />
                            <span>Mobile image</span>
                          </div>
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={banner.showVideo === true}
                              onChange={(e) => updateHeroBanner(idx, bannerIdx, { showVideo: e.target.checked })}
                            />
                            Show video button
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {key === 'destinations' && (
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Max items"
                  value={section.content.maxItems}
                  onChange={(e) => updateContent(idx, { maxItems: e.target.value })}
                />
              </div>
            )}

            {key === 'trending' && (
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Max items"
                  value={section.content.maxItems}
                  onChange={(e) => updateContent(idx, { maxItems: e.target.value })}
                />
              </div>
            )}

            {key === 'themes' && (
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
              </div>
            )}

            {key === 'listing' && (
              <div className="grid gap-3 md:grid-cols-4">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Default sort"
                  value={section.content.defaultSort}
                  onChange={(e) => updateContent(idx, { defaultSort: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Page size"
                  value={section.content.pageSize}
                  onChange={(e) => updateContent(idx, { pageSize: e.target.value })}
                />
              </div>
            )}

            {key === 'offers' && (
              <div className="grid gap-3 md:grid-cols-3">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Max items"
                  value={section.content.maxItems}
                  onChange={(e) => updateContent(idx, { maxItems: e.target.value })}
                />
              </div>
            )}

            {(key === 'reviews' || key === 'testimonials') && (
              <div className="grid gap-3 md:grid-cols-3">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Max items"
                  value={section.content.maxItems}
                  onChange={(e) => updateContent(idx, { maxItems: e.target.value })}
                />
              </div>
            )}

            {(key === 'journal' || key === 'articles') && (
              <div className="grid gap-3 md:grid-cols-3">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Max items"
                  value={section.content.maxItems}
                  onChange={(e) => updateContent(idx, { maxItems: e.target.value })}
                />
              </div>
            )}

            {(key === 'cta' || key === 'footercta') && (
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Heading"
                  value={section.content.heading}
                  onChange={(e) => updateContent(idx, { heading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Subheading"
                  value={section.content.subheading}
                  onChange={(e) => updateContent(idx, { subheading: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Button text"
                  value={section.content.buttonText}
                  onChange={(e) => updateContent(idx, { buttonText: e.target.value })}
                />
                <input
                  className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Button link"
                  value={section.content.buttonLink}
                  onChange={(e) => updateContent(idx, { buttonLink: e.target.value })}
                />
              </div>
            )}
          </div>
        )})}
      </div>
    </div>
  );
};

export default AdminPackageLanding;
